'use client';

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from 'react';
import {Dialog, DialogContent} from '@features/ui/components/dialog';
import {SubscriptionOptions} from './SubscriptionOptions';
import {PlanTypeSelector, type PlanType} from './PlanTypeSelector';
import {useSubscription} from './subscriptionHook';
import amplitudeService from '@features/analytics/amplitudeService';
import {toast} from 'sonner';
import {EnterpriseLink} from './EnterpriseLink';
import {PastDueModal} from './PastDueModal';

type PaywallSource =
  | 'generation-limit'
  | 'session-limit'
  | 'export-to-figma'
  | 'export-to-html-css'
  | 'export-to-external-agent'
  | 'home-screen'
  | 'canvas-toolbar'
  | 'credits-page'
  | 'settings';

interface PaywallModalContextType {
  /**
   * Opens the buy/upgrade paywall — unless the subscription is `past_due`, in
   * which case it opens the past-due modal instead (all purchases are pointless
   * until the existing payment clears).
   */
  openPaywallModal: (source: PaywallSource) => void;
  openPastDueModal: (source: string) => void;
}

const PaywallModalContext = createContext<PaywallModalContextType | null>(null);

export function usePaywallModal() {
  const context = useContext(PaywallModalContext);
  if (!context) {
    throw new Error(
      'usePaywallModal must be used within a PaywallModalProvider',
    );
  }
  return context;
}

export function PaywallModalProvider({children}: {children: React.ReactNode}) {
  const [isOpen, setIsOpen] = useState(false);
  const [planType, setPlanType] = useState<PlanType>('individual');
  const [tierAtOpen, setTierAtOpen] = useState<string | undefined>(undefined);
  const [sourceAtOpen, setSourceAtOpen] = useState<string | undefined>(
    undefined,
  );
  const [isPastDueOpen, setIsPastDueOpen] = useState(false);
  const {effectiveSubscription} = useSubscription();

  // Single attributed open event for the past-due modal, regardless of entry
  // point (indicator, paywall interception, generation block).
  const openPastDueModal = useCallback((source: string) => {
    setIsPastDueOpen(true);
    amplitudeService.logEvent('[Past Due] modal opened', {source});
  }, []);

  // Close modal when subscription changes (tier upgrade or individual → team)
  useEffect(() => {
    const currentTier = effectiveSubscription?.tier;
    const currentSource = effectiveSubscription?.subscriptionSource;
    if (
      isOpen &&
      currentTier &&
      tierAtOpen &&
      (currentTier !== tierAtOpen || currentSource !== sourceAtOpen)
    ) {
      const frameId = requestAnimationFrame(() => {
        setIsOpen(false);
        toast.success('Subscription activated!');
      });
      return () => cancelAnimationFrame(frameId);
    }
  }, [
    isOpen,
    effectiveSubscription?.tier,
    effectiveSubscription?.subscriptionSource,
    tierAtOpen,
    sourceAtOpen,
  ]);

  // Auto-close the past-due modal once payment resolves (isPastDue clears),
  // mirroring the paywall's auto-close on upgrade.
  useEffect(() => {
    if (isPastDueOpen && effectiveSubscription?.isPastDue === false) {
      const frameId = requestAnimationFrame(() => {
        setIsPastDueOpen(false);
        toast.success('Subscription reactivated!');
      });
      return () => cancelAnimationFrame(frameId);
    }
  }, [isPastDueOpen, effectiveSubscription?.isPastDue]);

  const openPaywallModal = useCallback(
    (source: PaywallSource) => {
      // Past due: buying more won't help until the failed payment clears.
      if (effectiveSubscription?.isPastDue) {
        openPastDueModal(source);
        return;
      }
      setPlanType(effectiveSubscription?.subscriptionSource ?? 'individual');
      setTierAtOpen(effectiveSubscription?.tier);
      setSourceAtOpen(effectiveSubscription?.subscriptionSource);
      setIsOpen(true);
      amplitudeService.logEvent('[Paywall Modal] opened', {source});
    },
    [
      effectiveSubscription?.tier,
      effectiveSubscription?.subscriptionSource,
      effectiveSubscription?.isPastDue,
      openPastDueModal,
    ],
  );

  const handlePlanTypeChange = (newPlanType: PlanType) => {
    setPlanType(newPlanType);
    amplitudeService.logEvent('[Paywall] plan type changed', {
      planType: newPlanType,
    });
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
  };

  const isTeamActive =
    effectiveSubscription?.subscriptionSource === 'team' &&
    effectiveSubscription.tier !== 'free';

  // When team is active, force team plan type and hide the selector
  const effectivePlanType = isTeamActive ? 'team' : planType;

  const shouldHideFreePlan =
    (effectivePlanType === 'individual' &&
      effectiveSubscription?.tier !== 'free') ||
    (effectivePlanType === 'team' && isTeamActive);

  return (
    <PaywallModalContext.Provider value={{openPaywallModal, openPastDueModal}}>
      {children}
      <PastDueModal open={isPastDueOpen} onOpenChange={setIsPastDueOpen} />
      <Dialog open={isOpen} onOpenChange={handleOpenChange}>
        <DialogContent className='flex max-h-[90vh] max-w-[1200px] flex-col'>
          <div className='flex flex-1 flex-col gap-6 overflow-auto pt-10'>
            {/* Header */}
            <h1 className='mx-auto max-w-sm text-center text-2xl font-semibold text-foreground'>
              Generate and export freely, at higher speeds
            </h1>

            {/* Plan Type Selector — hidden when team is active */}
            {!isTeamActive && (
              <div className='flex justify-center'>
                <PlanTypeSelector
                  value={planType}
                  onChange={handlePlanTypeChange}
                />
              </div>
            )}

            {/* Pricing Cards */}
            <SubscriptionOptions
              className='mx-auto items-start gap-6'
              planType={effectivePlanType}
              hideFreePlan={shouldHideFreePlan}
            />
          </div>

          {/* Enterprise Link - fixed at bottom */}
          <div className='pb-6 pt-4'>
            <EnterpriseLink />
          </div>
        </DialogContent>
      </Dialog>
    </PaywallModalContext.Provider>
  );
}
